import { getValidatedToken } from '@frameio/assets/fields/utils/colorMapping';
import type { SelectOption } from '@frameio/graphql-client/generated';
import {
  formatSystemFieldNumber,
  SYSTEM_FIELD_KEYS,
  type SystemFieldKey,
} from '@frameio/shared/fields/utils';
import { EMPTY_ARRAY } from '@frameio/utils';
import type { AssetResultMatch } from '../../types';
import {
  CommentFieldMatch,
  DateFieldMatch,
  MultiSelectFieldMatch,
  RatingFieldMatch,
  SelectFieldMatch,
  TextFieldMatch,
  ToggleFieldMatch,
  UserSingleFieldMatch,
} from './components';

type FieldMatchProps = {
  fieldMatch: AssetResultMatch;
  variant: 'block' | 'inline';
  truncate?: boolean;
};

const formatSystemMatchNumber = (value: number, fieldKey: SystemFieldKey) =>
  formatSystemFieldNumber(value, fieldKey) ?? value.toString();

const hasDefinedFields = <T, K extends keyof T>(
  obj: T,
  keys: K | K[]
): obj is T & { [P in K]-?: NonNullable<T[P]> } => {
  if (!Array.isArray(keys)) {
    return obj[keys] !== undefined && obj[keys] !== null;
  }
  return keys.every((key) => obj[key] !== undefined && obj[key] !== null);
};

export const FieldMatch = ({
  fieldMatch,
  variant,
  truncate = true,
}: FieldMatchProps) => {
  switch (fieldMatch?.__typename) {
    case 'FormatMatch': {
      if (!hasDefinedFields(fieldMatch, 'format')) return null;

      return (
        <TextFieldMatch
          text={fieldMatch.format}
          truncate={truncate}
          variant={variant}
        />
      );
    }

    case 'SourceFilenameMatch': {
      if (!hasDefinedFields(fieldMatch, 'sourceFilename')) return null;

      return (
        <TextFieldMatch
          highlights={(fieldMatch?.terms as string[]) ?? undefined}
          text={fieldMatch.sourceFilename}
          truncate={truncate}
          variant={variant}
        />
      );
    }

    case 'AssigneeMatch': {
      if (!hasDefinedFields(fieldMatch, 'assignee')) return null;

      return <UserSingleFieldMatch user={fieldMatch.assignee} />;
    }

    case 'CreatorMatch': {
      if (!hasDefinedFields(fieldMatch, 'creator')) return null;

      return <UserSingleFieldMatch user={fieldMatch.creator} />;
    }

    case 'KeywordsMatch': {
      if (!hasDefinedFields(fieldMatch, 'keywords')) return null;

      const maxTokenCount = variant === 'block' ? 4 : undefined;

      return (
        <MultiSelectFieldMatch
          maxTokenCount={maxTokenCount}
          options={fieldMatch.keywords as SelectOption[]}
        />
      );
    }

    case 'StatusMatch': {
      if (!hasDefinedFields(fieldMatch, 'status')) return null;

      return <SelectFieldMatch option={fieldMatch.status} />;
    }

    case 'RatingMatch': {
      if (!hasDefinedFields(fieldMatch, 'rating')) return null;

      const color = fieldMatch?.color
        ? getValidatedToken(fieldMatch.color)
        : undefined;

      return (
        <RatingFieldMatch
          color={color}
          rating={fieldMatch.rating}
          style={fieldMatch?.style ?? undefined}
        />
      );
    }

    case 'CommentCountMatch': {
      if (!hasDefinedFields(fieldMatch, 'commentCount')) return null;

      return (
        <TextFieldMatch
          text={fieldMatch.commentCount.toString()}
          variant={variant}
        />
      );
    }

    case 'PageCountMatch': {
      if (!hasDefinedFields(fieldMatch, 'pageCount')) return null;

      const pageCountText = formatSystemMatchNumber(
        fieldMatch.pageCount,
        SYSTEM_FIELD_KEYS.PAGE_COUNT
      );
      return <TextFieldMatch text={pageCountText} variant={variant} />;
    }

    case 'DurationMatch': {
      if (!hasDefinedFields(fieldMatch, 'duration')) return null;

      const durationText = formatSystemMatchNumber(
        fieldMatch.duration,
        SYSTEM_FIELD_KEYS.DURATION
      );
      return <TextFieldMatch text={durationText} variant={variant} />;
    }

    case 'FilesizeMatch': {
      if (!hasDefinedFields(fieldMatch, 'filesize')) return null;

      const filesizeText = formatSystemMatchNumber(
        fieldMatch.filesize,
        SYSTEM_FIELD_KEYS.FILE_SIZE
      );
      return <TextFieldMatch text={filesizeText} />;
    }

    case 'AudioSampleRateMatch': {
      if (!hasDefinedFields(fieldMatch, 'audioSampleRate')) return null;

      const audioSampleRateText = formatSystemMatchNumber(
        fieldMatch.audioSampleRate,
        SYSTEM_FIELD_KEYS.AUDIO_SAMPLE_RATE
      );
      return <TextFieldMatch text={audioSampleRateText} variant={variant} />;
    }

    case 'CommentMatch': {
      if (!hasDefinedFields(fieldMatch, 'comment')) return null;

      return (
        <CommentFieldMatch
          comment={fieldMatch.comment}
          snippet={fieldMatch.snippet ?? ''}
          terms={(fieldMatch.terms ?? EMPTY_ARRAY) as string[]}
          variant={variant}
        />
      );
    }

    case 'TranscriptMatch': {
      if (fieldMatch?.value === undefined || fieldMatch?.value === null)
        return null;

      const color = getValidatedToken(fieldMatch.color ?? '');
      return <ToggleFieldMatch color={color} value={fieldMatch.value} />;
    }

    case 'UploadedAtMatch': {
      if (!fieldMatch?.uploadedAt) return null;
      return <DateFieldMatch date={fieldMatch.uploadedAt} variant={variant} />;
    }

    case 'UpdatedAtMatch': {
      if (!fieldMatch?.updatedAt) return null;
      return <DateFieldMatch date={fieldMatch.updatedAt} variant={variant} />;
    }

    case 'DeletedAtMatch': {
      if (!fieldMatch?.deletedAt) return null;
      return <DateFieldMatch date={fieldMatch.deletedAt} variant={variant} />;
    }

    case 'InsertedAtMatch': {
      if (!fieldMatch?.insertedAt) return null;
      return <DateFieldMatch date={fieldMatch.insertedAt} variant={variant} />;
    }

    case 'NotesMatch': {
      if (!hasDefinedFields(fieldMatch, 'notes')) return null;

      return (
        <TextFieldMatch
          text={fieldMatch.notes}
          truncate={truncate}
          variant={variant}
        />
      );
    }

    case 'FileTypeMatch': {
      if (!hasDefinedFields(fieldMatch, 'fileType')) return null;

      return (
        <TextFieldMatch
          text={fieldMatch.fileType}
          truncate={truncate}
          variant={variant}
        />
      );
    }
    case 'BitDepthMatch': {
      if (!hasDefinedFields(fieldMatch, 'bitDepth')) return null;

      const bitDepthText = formatSystemMatchNumber(
        fieldMatch.bitDepth,
        SYSTEM_FIELD_KEYS.VISUAL_BIT_DEPTH
      );
      return (
        <TextFieldMatch
          text={bitDepthText}
          truncate={truncate}
          variant={variant}
        />
      );
    }
    case 'BitRateMatch': {
      if (!hasDefinedFields(fieldMatch, 'bitRate')) return null;

      const bitRateText = formatSystemMatchNumber(
        fieldMatch.bitRate,
        SYSTEM_FIELD_KEYS.BIT_RATE
      );
      return (
        <TextFieldMatch
          text={bitRateText}
          truncate={truncate}
          variant={variant}
        />
      );
    }
    case 'CodecMatch': {
      if (!hasDefinedFields(fieldMatch, 'codec')) return null;

      return (
        <TextFieldMatch
          text={fieldMatch.codec}
          truncate={truncate}
          variant={variant}
        />
      );
    }
    case 'ChannelsMatch': {
      if (!hasDefinedFields(fieldMatch, 'channels')) return null;

      const channelsText = formatSystemMatchNumber(
        fieldMatch.channels,
        SYSTEM_FIELD_KEYS.AUDIO_CHANNELS
      );
      return (
        <TextFieldMatch
          text={channelsText}
          truncate={truncate}
          variant={variant}
        />
      );
    }
    case 'FrameRateMatch': {
      if (!hasDefinedFields(fieldMatch, 'frameRate')) return null;

      const frameRateText = formatSystemMatchNumber(
        fieldMatch.frameRate,
        SYSTEM_FIELD_KEYS.FRAME_RATE
      );
      return (
        <TextFieldMatch
          text={frameRateText}
          truncate={truncate}
          variant={variant}
        />
      );
    }
    case 'HasAlphaChannelMatch': {
      if (!hasDefinedFields(fieldMatch, 'hasAlphaChannel')) return null;

      return <ToggleFieldMatch value={fieldMatch.hasAlphaChannel} />;
    }

    case 'ColorSpaceMatch': {
      if (!hasDefinedFields(fieldMatch, 'colorSpace')) return null;

      return (
        <TextFieldMatch
          text={fieldMatch.colorSpace}
          truncate={truncate}
          variant={variant}
        />
      );
    }
    case 'ResolutionWidthMatch': {
      if (!hasDefinedFields(fieldMatch, 'resolutionWidth')) return null;

      const resolutionWidthText = formatSystemMatchNumber(
        fieldMatch.resolutionWidth,
        SYSTEM_FIELD_KEYS.RESOLUTION_WIDTH
      );
      return (
        <TextFieldMatch
          text={resolutionWidthText}
          truncate={truncate}
          variant={variant}
        />
      );
    }
    case 'ResolutionHeightMatch': {
      if (!hasDefinedFields(fieldMatch, 'resolutionHeight')) return null;

      const resolutionHeightText = formatSystemMatchNumber(
        fieldMatch.resolutionHeight,
        SYSTEM_FIELD_KEYS.RESOLUTION_HEIGHT
      );
      return (
        <TextFieldMatch
          text={resolutionHeightText}
          truncate={truncate}
          variant={variant}
        />
      );
    }
    case 'DynamicRangeMatch': {
      if (!hasDefinedFields(fieldMatch, 'dynamicRange')) return null;

      return (
        <TextFieldMatch
          text={fieldMatch.dynamicRange}
          truncate={truncate}
          variant={variant}
        />
      );
    }

    default:
      return null;
  }
};
